import { getArtistFolderName, ARTISTS } from '../engine/constants'
import { useTheme } from '../hooks/useTheme'

type AuctionType = 'open' | 'one_offer' | 'hidden' | 'fixed_price' | 'double'

// Card interface
export interface CardData {
  id: string
  artist: string
  artistIndex: number
  cardIndex: number
  auctionType: AuctionType
}

interface CardProps {
  card: CardData
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  isHighlighted?: boolean  // Golden glow for matching cards in double auction
  isDisabled?: boolean     // Dimmed for non-matching cards in double auction
  isPartiallyHighlighted?: boolean  // Less highlighted state for non-selected eligible cards
  isTutorialHighlighted?: boolean  // Golden border for tutorial highlighting
  onClick?: () => void     // Click handler for card selection
}

// Auction icon component
export function AuctionIcon({ type, color, size = 16 }: { type: AuctionType; color: string; size?: number }) {
  const iconStyle = { width: size, height: size }

  switch (type) {
    case 'open':
      // Eye icon (simple, elegant)
      return (
        <svg {...iconStyle} viewBox="0 0 24 24" fill="none">
          <path
            d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5z"
            stroke={color}
            strokeWidth="2"
            fill="none"
          />
          <circle cx="12" cy="12" r="3" fill={color} />
        </svg>
      )
    case 'one_offer':
      // Star with "1" inside
      return (
        <svg {...iconStyle} viewBox="0 0 24 24" fill="none">
          <path
            d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
            stroke={color}
            strokeWidth="2"
            fill="none"
          />
          <text
            x="12"
            y="16"
            textAnchor="middle"
            fontSize="10"
            fontWeight="bold"
            fill={color}
          >
            1
          </text>
        </svg>
      )
    case 'hidden':
      // Padlock icon
      return (
        <svg {...iconStyle} viewBox="0 0 24 24" fill="none">
          <rect x="5" y="11" width="14" height="10" rx="2" stroke={color} strokeWidth="2" fill="none" />
          <path
            d="M7 11V7a5 5 0 0110 0v4"
            stroke={color}
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
          />
          <circle cx="12" cy="16" r="1.5" fill={color} />
          <path d="M12 17.5v1" stroke={color} strokeWidth="2" strokeLinecap="round" />
        </svg>
      )
    case 'fixed_price':
      return (
        <svg {...iconStyle} viewBox="0 0 24 24" fill="none">
          <rect x="4" y="4" width="16" height="16" rx="2" stroke={color} strokeWidth="2" />
          <text
            x="12"
            y="16"
            textAnchor="middle"
            fontSize="12"
            fontWeight="bold"
            fill={color}
          >
            $
          </text>
        </svg>
      )
    case 'double':
      // Gavel with hammer head and x2 text
      return (
        <svg {...iconStyle} viewBox="0 0 24 24" fill="none">
          {/* Gavel handle at 45 degrees - moved slightly left */}
          <line
            x1="5"
            y1="18"
            x2="15"
            y2="8"
            stroke={color}
            strokeWidth="3"
            strokeLinecap="round"
          />
          {/* Hammer head - rounded rectangle at top of handle, tilted 45 degrees - doubled in width - moved left */}
          <rect
            x="9"
            y="4"
            width="16"
            height="5"
            rx="2.5"
            fill={color}
            transform="rotate(45 17 6.5)"
          />
          {/* x2 text - moved left */}
          <text
            x="16"
            y="21"
            textAnchor="middle"
            fontSize="6"
            fontWeight="bold"
            fill={color}
          >
            x2
          </text>
        </svg>
      )
    default:
      return null
  }
}

// Artwork image with fallback to placeholder gradient
export function CardArtwork({ artistIndex, cardIndex }: { artistIndex: number; cardIndex: number }) {
  const seed = artistIndex * 100 + cardIndex

  const artistFolder = getArtistFolderName(artistIndex)
  const imagePath = `/assets/artworks/${artistFolder}/${artistFolder}_${String(cardIndex).padStart(2, '0')}.webp`

  const gradients: Record<number, string> = {
    0: `linear-gradient(${135 + (seed % 90)}deg,
        hsl(${35 + (seed % 20)}, 75%, ${35 + (seed % 15)}%) 0%,
        hsl(${25 + (seed % 30)}, 65%, ${25 + (seed % 20)}%) 100%)`,
    1: `linear-gradient(${180 + (seed % 60)}deg,
        hsl(${0 + (seed % 15)}, 70%, ${30 + (seed % 15)}%) 0%,
        hsl(${350 + (seed % 20)}, 60%, ${20 + (seed % 15)}%) 100%)`,
    2: `linear-gradient(${160 + (seed % 80)}deg,
        hsl(${175 + (seed % 20)}, 55%, ${30 + (seed % 15)}%) 0%,
        hsl(${185 + (seed % 25)}, 45%, ${20 + (seed % 20)}%) 100%)`,
    3: `linear-gradient(${140 + (seed % 70)}deg,
        hsl(${130 + (seed % 30)}, 50%, ${30 + (seed % 15)}%) 0%,
        hsl(${110 + (seed % 40)}, 40%, ${20 + (seed % 20)}%) 100%)`,
    4: `linear-gradient(${150 + (seed % 90)}deg,
        hsl(${280 + (seed % 30)}, 55%, ${35 + (seed % 15)}%) 0%,
        hsl(${260 + (seed % 40)}, 45%, ${20 + (seed % 20)}%) 100%)`,
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: gradients[artistIndex % 5],
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Try to load image, hide on error to show gradient */}
      <img
        src={imagePath}
        alt=""
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
        onError={(e) => {
          e.currentTarget.style.display = 'none'
        }}
      />
      {/* Abstract shapes overlay (subtle) */}
      <svg
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          opacity: 0.15,
          pointerEvents: 'none',
        }}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <circle
          cx={25 + (seed % 50)}
          cy={30 + (seed % 40)}
          r={8 + (seed % 15)}
          fill="rgba(255,255,255,0.15)"
        />
        <rect
          x={55 + (seed % 25)}
          y={50 + (seed % 30)}
          width={12 + (seed % 18)}
          height={15 + (seed % 20)}
          fill="rgba(0,0,0,0.1)"
          transform={`rotate(${(seed % 30) - 15} ${60 + (seed % 25)} ${55 + (seed % 30)})`}
        />
      </svg>
    </div>
  )
}

// Size configurations (heights adjusted to maintain 3:4 aspect ratio for artwork)
const sizeConfig = {
  sm: { width: '80px', height: '127px', headerHeight: '20px', fontSize: 10, iconSize: 14 },
  md: { width: '120px', height: '184px', headerHeight: '24px', fontSize: 12, iconSize: 16 },
  lg: { width: '160px', height: '243px', headerHeight: '30px', fontSize: 14, iconSize: 18 },
  xl: { width: '200px', height: '303px', headerHeight: '36px', fontSize: 16, iconSize: 20 },
}

export function Card({
  card,
  size = 'md',
  className = '',
  isHighlighted = false,
  isDisabled = false,
  isPartiallyHighlighted = false,
  isTutorialHighlighted = false,
  onClick
}: CardProps) {
  const theme = useTheme()
  const artistName = ARTISTS[card.artistIndex]
  const artistColor = theme.artists[artistName]
  const config = sizeConfig[size]

  // Base styles
  const baseStyles = {
    width: config.width,
    height: config.height,
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    overflow: 'hidden',
    flexShrink: 0,
    position: 'relative' as const,
    transition: 'all 0.3s ease',
    cursor: onClick ? 'pointer' : 'default',
    // Tutorial highlight: golden border with glow
    border: isTutorialHighlighted ? '3px solid #fbbf24' : 'none',
    boxShadow: isTutorialHighlighted
      ? '0 0 20px rgba(251, 191, 36, 0.5), 0 0 40px rgba(251, 191, 36, 0.3), 0 8px 32px rgba(0,0,0,0.6)'
      : '0 8px 32px rgba(0,0,0,0.6)',
    transform: isTutorialHighlighted ? 'scale(1.03)' : 'none',
  }

  // Highlighted styles (golden glow + animation)
  const highlightedStyles = isHighlighted ? {
    boxShadow: `
      0 0 20px rgba(251, 191, 36, 0.6),
      0 0 40px rgba(251, 191, 36, 0.4),
      0 8px 32px rgba(0,0,0,0.6)
    `,
    transform: 'scale(1.05)',
    '&::before': {
      content: '""',
      position: 'absolute' as const,
      inset: -2,
      borderRadius: 10,
      padding: 2,
      background: 'linear-gradient(45deg, #fbbf24, #f59e0b, #fbbf24)',
      mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      maskComposite: 'xor',
      animation: 'pulse 2s ease-in-out infinite',
    }
  } : {}

  // Partially highlighted styles (dimmer glow for non-selected eligible cards)
  const partiallyHighlightedStyles = isPartiallyHighlighted ? {
    boxShadow: `
      0 0 12px rgba(251, 191, 36, 0.3),
      0 0 24px rgba(251, 191, 36, 0.2),
      0 8px 32px rgba(0,0,0,0.6)
    `,
    transform: 'scale(1.02)',
  } : {}

  // Disabled styles (dimmed + grayscale)
  const disabledStyles = isDisabled ? {
    opacity: 0.4,
    filter: 'grayscale(80%)',
    cursor: 'not-allowed',
    transform: 'scale(0.95)',
  } : {}

  // Hover effects for highlighted cards
  const hoverStyles = isHighlighted && onClick ? {
    '&:hover': {
      transform: 'scale(1.08)',
      boxShadow: `
        0 0 25px rgba(251, 191, 36, 0.8),
        0 0 50px rgba(251, 191, 36, 0.6),
        0 8px 32px rgba(0,0,0,0.6)
      `,
    }
  } : isPartiallyHighlighted && onClick ? {
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: `
        0 0 20px rgba(251, 191, 36, 0.6),
        0 0 40px rgba(251, 191, 36, 0.4),
        0 8px 32px rgba(0,0,0,0.6)
      `,
    }
  } : {}

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
        `}
      </style>
      <div
        className={className}
        onClick={isDisabled ? undefined : onClick}
        style={{
          ...baseStyles,
          ...highlightedStyles,
          ...partiallyHighlightedStyles,
          ...disabledStyles,
        }}
      >
      {/* Header */}
      <div
        style={{
          height: config.headerHeight,
          backgroundColor: artistColor.primary,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          paddingLeft: 8,
          paddingRight: 8,
        }}
      >
        <AuctionIcon type={card.auctionType} color="#000000" size={config.iconSize} />
        <span
          style={{
            color: '#000000',
            fontSize: config.fontSize,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {artistName}
        </span>
      </div>

      {/* Artwork */}
      <div style={{ height: `calc(${config.height} - ${config.headerHeight})` }}>
        <CardArtwork artistIndex={card.artistIndex} cardIndex={card.cardIndex} />

        {/* Selection indicator for highlighted cards */}
        {isHighlighted && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 28,
              height: 28,
              backgroundColor: 'rgba(251, 191, 36, 0.9)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
              border: '2px solid #1a1a1a',
            }}
          >
            <svg width={16} height={16} viewBox="0 0 16 16" fill="none">
              <path
                d="M3 8l3 3 7-7"
                stroke="#1a1a1a"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}

        {/* "Twofer" hint overlay - show for both highlighted and partially highlighted */}
        {(isHighlighted || isPartiallyHighlighted) && (
          <div
            style={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: '#fbbf24',
              fontSize: '10px',
              fontWeight: 'bold',
              padding: '4px 8px',
              borderRadius: 4,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              opacity: isPartiallyHighlighted ? 0.6 : 1,
            }}
          >
            Offer This
          </div>
        )}
      </div>
      </div>
    </>
  )
}